/**
 * @fileoverview Forgot-password form.
 * @description The email form itself: client-side validation (via React Hook Form +
 * forgotPasswordFormSchema), submit loading/error state, and the confirmation view swapped in once
 * the reset-link request succeeds. Mirrors LoginForm's structure (validate -> mutate -> render
 * mutation state) for the same request flow, one step removed. The two views are split into small
 * internal components so neither grows past a readable size.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useState, type FormEvent } from 'react'
import { useForm, type UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import checkIcon from '@/assets/svg/login/icon-check.svg'
import mailIcon from '@/assets/svg/login/icon-mail.svg'
import { useRequestPasswordReset } from '@/features/auth/hooks/useRequestPasswordReset'
import {
  forgotPasswordFormSchema,
  type ForgotPasswordFormValues,
} from '@/features/auth/schemas/forgot-password-form.schema'
import Button from '@/shared/components/atoms/Button'
import FormField from '@/shared/components/molecules/FormField'
import { ROUTES } from '@/shared/constants/routes.constants'

interface SuccessViewProps {
  email: string
  onTryAgain: () => void
}

/**
 * @description Renders the "reset link sent" confirmation shown after a successful submit.
 * @param {SuccessViewProps} props - The submitted email and the "try again" handler.
 * @param {string} props.email - The email address the reset link was sent to.
 * @param {() => void} props.onTryAgain - Clears the mutation state so the request form shows
 * again.
 */
function SuccessView({ email, onTryAgain }: SuccessViewProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div className="flex flex-col items-center text-center" data-testid="forgot-password-success">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
        <img src={checkIcon} alt="" className="h-6 w-6" />
      </div>
      <h1
        data-testid="forgot-password-success-title"
        className="mt-5 text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[30px]"
      >
        {t('forgotPassword.success.title')}
      </h1>
      <p className="mt-1.5 text-[15px] text-slate sm:text-base">
        {t('forgotPassword.success.description')}
      </p>
      <p
        className="mt-1 text-[15px] font-semibold text-ink"
        data-testid="forgot-password-success-email"
      >
        {email}
      </p>

      <div className="mt-6 w-full">
        <Button
          onClick={() => void navigate(ROUTES.LOGIN)}
          data-testid="forgot-password-success-login-button"
        >
          {t('forgotPassword.success.loginAgain')}
        </Button>
      </div>

      <p className="mt-4 text-[13px] text-slate">
        {t('forgotPassword.success.resendPromptPrefix')}{' '}
        <button
          type="button"
          data-testid="forgot-password-success-try-again-button"
          className="cursor-pointer font-medium text-primary underline"
          onClick={onTryAgain}
        >
          {t('forgotPassword.success.tryAgain')}
        </button>
        .
      </p>
    </div>
  )
}

interface RequestViewProps {
  register: UseFormRegister<ForgotPasswordFormValues>
  fieldError: string | undefined
  isPending: boolean
  errorMessage: string | undefined
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

/**
 * @description Renders the email-entry form requesting a password-reset link.
 * @param {RequestViewProps} props - The form's register function, current error/pending state,
 * and submit handler.
 * @param {UseFormRegister<ForgotPasswordFormValues>} props.register - React Hook Form's register
 * function, bound to the email field.
 * @param {string} [props.fieldError] - Validation message for the email field, if any.
 * @param {boolean} props.isPending - Whether the reset request is in flight.
 * @param {string} [props.errorMessage] - The request's server error message, if any.
 * @param {(event: FormEvent<HTMLFormElement>) => void} props.onSubmit - The form's submit handler.
 */
function RequestView({
  register,
  fieldError,
  isPending,
  errorMessage,
  onSubmit,
}: RequestViewProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div>
      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/15 bg-linear-to-br from-blue-50 to-blue-100">
        <img src={mailIcon} alt="" className="h-5 w-5" />
      </div>

      <h1
        data-testid="forgot-password-title"
        className="mt-5 text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[30px]"
      >
        {t('forgotPassword.title')}
      </h1>
      <p className="mt-1.5 text-[15px] text-slate sm:text-base">
        {t('forgotPassword.description')}
      </p>

      <form
        className="mt-6 flex flex-col gap-6"
        onSubmit={onSubmit}
        noValidate
        data-testid="forgot-password-form"
      >
        <FormField
          id="email"
          data-testid="forgot-password-email-input"
          label={t('forgotPassword.emailLabel')}
          type="email"
          placeholder={t('forgotPassword.emailPlaceholder')}
          autoComplete="email"
          error={fieldError}
          {...register('email')}
        />

        {errorMessage && (
          <p
            role="alert"
            className="text-[14px] text-red-600"
            data-testid="forgot-password-error-message"
          >
            {errorMessage}
          </p>
        )}

        <div className="flex flex-col gap-3">
          <Button type="submit" isLoading={isPending} data-testid="forgot-password-submit-button">
            {isPending ? t('forgotPassword.submitPending') : t('forgotPassword.submitIdle')}
          </Button>
          <Button
            variant="secondary"
            type="button"
            onClick={() => void navigate(ROUTES.LOGIN)}
            data-testid="forgot-password-back-to-sign-in-button"
          >
            {t('forgotPassword.backToSignIn')}
          </Button>
        </div>
      </form>
    </div>
  )
}

/**
 * @component
 */
function ForgotPasswordForm() {
  const { t } = useTranslation()
  const [submittedEmail, setSubmittedEmail] = useState('')
  const resetMutation = useRequestPasswordReset()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: { email: '' },
  })

  /**
   * @description Submits the password-reset request with the schema-validated email, keeping a
   * copy of it for the confirmation view (the field itself lives in React Hook Form state, which
   * doesn't need to be read back once submitted).
   * @param {ForgotPasswordFormValues} values - The validated email.
   */
  const onValid = (values: ForgotPasswordFormValues) => {
    setSubmittedEmail(values.email)
    resetMutation.mutate(values)
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid. Wrapped so the DOM
   * onSubmit handler returns void rather than the Promise React Hook Form's handleSubmit produces.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  if (resetMutation.isSuccess) {
    return <SuccessView email={submittedEmail} onTryAgain={() => resetMutation.reset()} />
  }

  return (
    <RequestView
      register={register}
      fieldError={errors.email && t(errors.email.message ?? '')}
      isPending={resetMutation.isPending}
      errorMessage={resetMutation.isError ? resetMutation.error.message : undefined}
      onSubmit={onSubmit}
    />
  )
}

export default ForgotPasswordForm
